import { useSuspenseQuery } from '@tanstack/react-query';
import { SnippetLanguage } from 'components/snippet/SnippetLanguage';
import { DEFAULT_LANGUAGE } from 'components/snippet/constants';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import html from 'highlight.js/lib/languages/xml';
import babelParser from 'prettier/plugins/babel';
import estreeParser from 'prettier/plugins/estree';
import htmlParser from 'prettier/plugins/html';
import prettier from 'prettier/standalone';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('html', html);
hljs.registerLanguage('json', json);

const ERROR_TEXT = `
/**
  Something went wrong trying to get your code snippet.
  Please try again or please reach out to customer support.
**/
`;

interface UseSnippetProps {
    language?: SnippetLanguage;
    text?: string;
}

const useSnippet = ({ language = DEFAULT_LANGUAGE, text = '' }: UseSnippetProps) => {
    const isError = typeof text !== 'string';
    text = isError ? ERROR_TEXT : text;
    language = isError ? SnippetLanguage.JS : language;

    const { data: formattedText = '' } = useSuspenseQuery({
        queryKey: ['formattedText', text],
        queryFn: () => prettierFactory(language)(text),
    });

    const highlightedText = hljsFactory(language)(text).value;
    const highlightedAndFormattedText = hljsFactory(language)(formattedText).value;

    return {
        formattedText,
        highlightedText,
        highlightedAndFormattedText,
    };
};

const prettierFactory = (language = DEFAULT_LANGUAGE) => {
    return (text: string) => {
        switch (language) {
            case 'html': {
                return prettier.format(text, {
                    parser: 'html',
                    plugins: [babelParser, htmlParser],
                });
            }
            case 'json': {
                return prettier.format(text, {
                    parser: 'json',
                    plugins: [babelParser, estreeParser],
                });
            }
            case 'js':
            default: {
                return prettier.format(text, {
                    parser: 'babel',
                    plugins: [babelParser, estreeParser],
                });
            }
        }
    };
};

const hljsFactory = (language = DEFAULT_LANGUAGE) => {
    return (text: string) => {
        switch (language) {
            case 'html': {
                return hljs.highlightAuto(text, ['html']);
            }
            case 'json': {
                return hljs.highlightAuto(text, ['json']);
            }
            case 'js':
            default: {
                return hljs.highlightAuto(text, ['javascript']);
            }
        }
    };
};

export { useSnippet };
